import { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { Decimal } from 'decimal.js';
import { getIsPointsPay } from 'store/ui/uiSelectors';
import { POINTS_EARN_ENABLED } from 'config';
import type { AvaPointsEarn } from 'types/ava';

interface UsePointsEarnedInput {
  pointsEarned: AvaPointsEarn;
}

interface UsePointsEarnedReturn {
  onCashPaymentAmountChange: (args: { cashAmount: Decimal }) => void;
  pointsEarned: AvaPointsEarn | null;
}

const usePointsEarnedDisabled = (): UsePointsEarnedReturn => ({
  onCashPaymentAmountChange: noop,
  pointsEarned: null,
});

const usePointsEarnedEnabled = ({ pointsEarned: initialPointsEarned }: UsePointsEarnedInput): UsePointsEarnedReturn => {
  const isPointsPay = useSelector(getIsPointsPay);
  const { propertyPpd, maxQffEarnPpd = 0, maxQbrEarnPpd = 0, promotionMultiplier = 0, qffPoints, qbrPoints } = initialPointsEarned;

  // assign to the minimal attributes we want to expose to prevent use of
  // attributes that won't be available after a recalculation
  const minimalPointsEarned: AvaPointsEarn = useMemo(
    () => ({
      qffPoints: {
        base: qffPoints.base,
        bonus: qffPoints.bonus ?? 0,
        total: qffPoints.total,
        qffPointsClub: qffPoints.qffPointsClub ?? 0,
      },
      qbrPoints: {
        total: qbrPoints.total,
      },
      maxQffEarnPpd,
      propertyPpd,
      maxQbrEarnPpd,
      promotionMultiplier,
    }),
    [
      qffPoints.base,
      qffPoints.bonus,
      qffPoints.total,
      qffPoints.qffPointsClub,
      qbrPoints.total,
      maxQffEarnPpd,
      propertyPpd,
      maxQbrEarnPpd,
      promotionMultiplier,
    ],
  );

  const [pointsEarned, setPointsEarned] = useState<AvaPointsEarn>(minimalPointsEarned);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onCashPaymentAmountChange = useCallback(
    debounce(({ cashAmount }) => {
      setPointsEarned({
        maxQffEarnPpd,
        maxQbrEarnPpd,
        promotionMultiplier,
        propertyPpd,
        qffPoints: {
          base: cashAmount.times(propertyPpd).toNumber(),
          bonus: 0, // You may want to recalculate this if needed
          total: cashAmount.times(maxQffEarnPpd).toNumber(),
          qffPointsClub: 0, // You may want to recalculate this if needed
        },
        qbrPoints: {
          total: cashAmount.times(maxQbrEarnPpd).toNumber(),
        },
      });
    }, 200),
    [maxQbrEarnPpd, maxQffEarnPpd, promotionMultiplier, propertyPpd],
  );

  useEffect(() => {
    if (!isPointsPay) {
      setPointsEarned(minimalPointsEarned);
    }
  }, [minimalPointsEarned, isPointsPay]);

  return { onCashPaymentAmountChange, pointsEarned };
};

const usePointsEarned: (args: UsePointsEarnedInput) => UsePointsEarnedReturn = POINTS_EARN_ENABLED
  ? usePointsEarnedEnabled
  : usePointsEarnedDisabled;

export default usePointsEarned;
